/**
 * Map and object-list display helpers (labels, colors, addresses, filter ordering).
 * Domain constants and workflow logic live in `@core/shared-bridge` — import those from the bridge,
 * not from here, unless this file already re-exports them for convenience.
 * See `docs/frontend/import-conventions.md`.
 */
import {
  CZECH_STATUS,
  STATUS_RED,
  STATUS_YELLOW,
  STATUS_GREEN,
  getObjectStatusColor,
  getPhaseName,
  getPhaseColor,
  getLineStyle,
  hasWorkflow,
  getWorkflowForType,
  getAllPhasesForType,
  getPhaseInfoByCode,
  resolvePhaseLabel,
  PHASE_COLORS,
  PHASE_NAME,
  getPhaseColorForDrawing,
  getObjectTypeName,
  MAP_OBJECT_LINE_OPACITY,
  MAP_OBJECT_LINE_OPACITY_INFORMATIVE_ROUTE,
  MAP_OBJECT_LINE_WEIGHT_THICK,
  MAP_OBJECT_LINE_WEIGHT_THIN,
  MAP_OBJECT_LINE_WEIGHT_DEFAULT,
  type DrawingId,
  type ObjectStatusBadgeType,
  normalizeObjectTypeForLookup,
  resolveMapDisplayPhaseCode,
  type WorkflowPhase,
  formatBudovaAddressLabel,
  toBudovaAcquisitionAddress,
  type BudovaAddressLabelInput,
  formatHouseNumberLabel,
  type TypCisla,
  resolveBudovaPripojenaPhaseColorOverride,
} from '@core/shared-bridge';

/** Re-export shared status colors for map/UI. */
export { getStatusColor } from '@core/shared-bridge';

export {
  getPhaseByCode,
  getPhaseName,
  resolvePhaseLabel,
  resolveAttachmentPhaseLabel,
  parseWorkflowVariant,
  getPreviousPhaseCodes,
  getNextPhaseCodes,
  getWorkflowForType,
  getAllPhasesForType,
} from '@core/shared-bridge';

export { getObjectTypeName };

/** Barvy z @cubby/shared pro mapu — jednotné fallbacky místo hardcoded hex. */
export const MAP_PHASE_HIGHLIGHT_COLOR = PHASE_COLORS.ORANGE;
export const MAP_NO_STATUS_COLOR = PHASE_COLORS.GRAY;

const OBJECT_STATUS_TEXT_MAP: Record<string, string> = {
  [CZECH_STATUS.NO_INFORMATION.code]: CZECH_STATUS.NO_INFORMATION.czechDescription,
  [CZECH_STATUS.IN_PROGRESS.code]: CZECH_STATUS.IN_PROGRESS.czechDescription,
  [CZECH_STATUS.COMPLETE.code]: CZECH_STATUS.COMPLETE.czechDescription,
};

const STATUS_BADGE_BY_CODE: Record<string, ObjectStatusBadgeType> = {
  [CZECH_STATUS.NO_INFORMATION.code]: 'no-info',
  [CZECH_STATUS.IN_PROGRESS.code]: 'in-progress',
  [CZECH_STATUS.COMPLETE.code]: 'complete',
};

/**
 * Get Czech text for object status
 * @param status - Object status code
 * @returns Czech status text or original status if not found
 */
export function getObjectStatusText(status: string): string {
  return OBJECT_STATUS_TEXT_MAP[status] || status;
}

/**
 * Get combined Czech text for object type and status.
 * Example: "Nevykopaný" for VYKOP + odpovídající fáze.
 * BUDOVA bez fáze / v Nepřipojený → zobrazí se jako „Nepřipojený“ (první fáze).
 * @param objectType - Object type code
 * @param status - Object status/phase code
 * @returns Combined Czech text or fallback to "typeName (status)"
 */
export function getObjectTypeAndStatusText(
  objectType: string,
  status: string | null,
  _subtype?: string | null,
  _metadata?: unknown,
): string {
  if (!objectType) return objectType;
  if (objectType === 'BUDOVA' && !status) {
    return PHASE_NAME.BUDOVA_NEPRIPOJENY;
  }
  if (!status) {
    return getObjectTypeName(objectType) || objectType;
  }

  const label = resolvePhaseLabel(status);
  if (label !== status) {
    return label;
  }

  const typeName = getObjectTypeName(objectType);
  return typeName || 'Neznámý stav';
}

/**
 * Map object status code to StatusBadgeComponent status type.
 * Supports both Czech names and status codes.
 * @param status - Object status code or Czech name
 * @returns Status badge type
 */
export function getStatusType(status: string): ObjectStatusBadgeType {
  if (status === STATUS_RED) return 'no-info';
  if (status === STATUS_YELLOW) return 'in-progress';
  if (status === STATUS_GREEN) return 'complete';
  return STATUS_BADGE_BY_CODE[status] || 'no-info';
}


/**
 * Format coordinates for display
 * @param coords - Coordinates as [lng, lat] or [lat, lng]
 * @returns Formatted string "lat, lng"
 */
export function formatCoordinates(coords?: [number, number]): string {
  if (!coords) return '-';
  return `${coords[1].toFixed(6)}, ${coords[0].toFixed(6)}`;
}

/** Minimum tvar adresy připojovaného objektu z různých endpointů (work-report, activity, popup). */
export interface ObjectAddressLite {
  cisloPopisne: string;
  /** 'P' = číslo popisné, 'E' = číslo evidenční. Starší API ho neposílá — default 'P'. */
  typCisla?: TypCisla | null;
  orientacni?: string | null;
  ulice?: string | null;
}

/** BUDOVA uses own address; dokop VYKOP / branch SPOJKOVISTE use linked BUDOVA address. */
export function resolveObjectListAddress(
  obj: {
    type?: string | null;
    addressPoint?: ObjectAddressLite | null;
    parentBudovaAddress?: ObjectAddressLite | null;
  } | null | undefined,
): ObjectAddressLite | null | undefined {
  if (!obj) return null;
  if (obj.type === 'BUDOVA') return obj.addressPoint;
  if (obj.type === 'SPOJKOVISTE' || obj.type === 'VYKOP') {
    return obj.parentBudovaAddress ?? null;
  }
  return null;
}

/**
 * Formátuje adresu připojovaného objektu pro inline zobrazení v seznamech (např. „č.p. 23/5, Hlavní").
 * Pokud chybí ulice nebo orientační číslo, příslušnou část vynecháme — funkce nikdy nevrací oddělovač
 * bez obsahu. Vrací prázdný řetězec, pokud nejsou žádná použitelná data.
 */
export function formatObjectAddress(addr: ObjectAddressLite | null | undefined): string {
  if (!addr) return '';
  const cp = addr.cisloPopisne?.trim() ?? '';
  const orient = addr.orientacni?.trim() ?? '';
  const ulice = addr.ulice?.trim() ?? '';
  const numberLabel = formatHouseNumberLabel(addr.typCisla, cp, orient);
  if (numberLabel && ulice) return `${numberLabel}, ${ulice}`;
  return numberLabel || ulice;
}

function mapObjectToBudovaAddressLabelInput(obj: {
  type?: string | null;
  addressPoint?: {
    cisloPopisne?: string;
    typCisla?: TypCisla | string | null;
    orientacni?: string | null;
    ulice?: string | null;
    obec?: string | null;
    psc?: string | null;
  } | null;
  parentBudovaAddress?: {
    cisloPopisne?: string;
    typCisla?: TypCisla | string | null;
    orientacni?: string | null;
    ulice?: string | null;
    obec?: string | null;
    psc?: string | null;
  } | null;
  acquisition?: unknown;
  parcelNumber?: string | null;
}): BudovaAddressLabelInput {
  const ap =
    obj.type === 'BUDOVA'
      ? obj.addressPoint
      : obj.type === 'SPOJKOVISTE' || obj.type === 'VYKOP'
        ? obj.parentBudovaAddress
        : obj.addressPoint;
  return {
    addressPoint: ap ?? null,
    acquisitionAddress: toBudovaAcquisitionAddress(obj.acquisition),
    parcelNumber: obj.parcelNumber ?? null,
  };
}

/** VYKOP list: street from path-advance batch (fallback parent BUDOVA ulice). */
export function resolveVykopListStreetLabel(obj: {
  type?: string | null;
  vykopStreetName?: string | null;
  vykopStreetPending?: boolean | null;
  parentBudovaAddress?: { ulice?: string | null } | null;
}): string {
  if (obj.type !== 'VYKOP') return '';
  const fromBatch = obj.vykopStreetName?.trim();
  if (fromBatch) return fromBatch;
  if (obj.vykopStreetPending) return 'počítá se…';
  return (obj.parentBudovaAddress?.ulice ?? '').trim();
}

/** VYKOP list: ulice (batch nebo rodič) + obec (bez PSČ). */
export function formatVykopStreetWithObecLabel(
  street: string | null | undefined,
  opts?: {
    pending?: boolean;
    psc?: string | null;
    obec?: string | null;
  },
): string {
  if (opts?.pending) return 'počítá se…';
  const s = (street ?? '').trim();
  const cityPart = (opts?.obec ?? '').trim();
  if (s && cityPart) return `${s}, ${cityPart}`;
  return s || cityPart || '—';
}

function resolveVykopListObecPart(obj: {
  parentBudovaAddress?: { obec?: string | null; psc?: string | null } | null;
  vykopStreetObec?: string | null;
  vykopStreetPsc?: string | null;
  acquisition?: unknown;
}): { psc: string; obec: string } {
  const ap = obj.parentBudovaAddress;
  return {
    psc: (ap?.psc ?? obj.vykopStreetPsc ?? '').trim(),
    obec: (ap?.obec ?? obj.vykopStreetObec ?? toBudovaAcquisitionAddress(obj.acquisition)?.mesto ?? '').trim(),
  };
}

/** Objects list „Adresa“ — full address with obec; VYKOP = batch ulice + obec (bez č.p.). */
export function formatObjectsListAdresaLabel(obj: {
  type?: string | null;
  vykopStreetName?: string | null;
  vykopStreetPending?: boolean | null;
  vykopStreetObec?: string | null;
  vykopStreetPsc?: string | null;
  parentBudovaAddress?: {
    cisloPopisne?: string;
    typCisla?: TypCisla | string | null;
    orientacni?: string | null;
    ulice?: string | null;
    obec?: string | null;
    psc?: string | null;
  } | null;
  addressPoint?: {
    cisloPopisne?: string;
    typCisla?: TypCisla | string | null;
    orientacni?: string | null;
    ulice?: string | null;
    obec?: string | null;
    psc?: string | null;
  } | null;
  acquisition?: unknown;
  parcelNumber?: string | null;
}): string {
  if (obj.type === 'VYKOP') {
    const street = resolveVykopListStreetLabel(obj);
    const { psc, obec } = resolveVykopListObecPart(obj);
    return formatVykopStreetWithObecLabel(street, {
      pending: obj.vykopStreetPending === true,
      psc,
      obec,
    });
  }
  const label = formatBudovaAddressLabel(mapObjectToBudovaAddressLabelInput(obj));
  return label ?? '—';
}

/**
 * Format date for display
 * @param date - Date to format
 * @returns Formatted date string in Czech locale
 */
/** Časové pásmo pro zobrazení dat (ČR). */
const DISPLAY_TIMEZONE = 'Europe/Prague';

export function formatDate(date?: Date | string): string {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('cs-CZ', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: DISPLAY_TIMEZONE,
  });
}

/**
 * Get Tailwind CSS classes for status background color
 * @param status - Object/polygon status (Czech name or code)
 * @returns Tailwind CSS classes for background and border
 */
export function getStatusBackgroundColorClasses(status: string): string {
  const colorMap: Record<string, string> = {
    [STATUS_RED]: 'bg-error-50 border-error-200',
    [STATUS_YELLOW]: 'bg-warning-50 border-warning-200',
    [STATUS_GREEN]: 'bg-success-50 border-success-200',
    [CZECH_STATUS.NO_INFORMATION.code]: 'bg-error-50 border-error-200',
    [CZECH_STATUS.IN_PROGRESS.code]: 'bg-warning-50 border-warning-200',
    [CZECH_STATUS.COMPLETE.code]: 'bg-success-50 border-success-200',
  };
  return colorMap[status] || 'bg-neutral-50 border-neutral-200';
}

const DEFAULT_STATUS_COLOR = PHASE_COLORS.GRAY;

/**
 * Get extended status color: workflow phase color when available, else polygon status colors.
 * @param status - Workflow phase code or polygon status (Nezačato/Probíhá/Hotové), or null
 * @param objectType - Optional object type for phase lookup
 */
export function getExtendedStatusColor(status: string | null | undefined, objectType?: string): string {
  if (status == null || status === '') {
    return DEFAULT_STATUS_COLOR;
  }
  if (objectType && hasWorkflow(objectType)) {
    const phaseColor = getPhaseColor(objectType, status);
    if (phaseColor !== DEFAULT_STATUS_COLOR) return phaseColor;
  }
  const info = getPhaseInfoByCode(status);
  if (info) {
    const key = info.phase.colors.general;
    return key ? PHASE_COLORS[key] : PHASE_COLORS.RED;
  }
  return getObjectStatusColor(status, objectType);
}

/**
 * Get status color for map rendering, optionally by drawing (layer).
 * When drawingId is set, uses drawing-specific phase colors; otherwise getExtendedStatusColor.
 * objectType je uppercase; barvy výkresu používají kanonické klíče (např. `VYKOP`).
 * `metadata` — zachováno pro kompatibilitu volajících (barva fáze je určena kódem fáze).
 * Handles null status (INFO, UNASSIGNED).
 */
export function getStatusColorForMap(
  status: string | null | undefined,
  objectType: string,
  drawingId?: string,
  metadata?: unknown,
  options?: { digitalniCesta?: number | null },
): string {
  const normalizedType = normalizeObjectTypeForLookup(objectType);
  const displayPhase = resolveMapDisplayPhaseCode(normalizedType, status);
  if (normalizedType === 'BUDOVA' && displayPhase) {
    const override = resolveBudovaPripojenaPhaseColorOverride(
      displayPhase,
      options?.digitalniCesta,
    );
    if (override) return override;
  }
  if (drawingId && displayPhase) {
    const color = getPhaseColorForDrawing(
      drawingId as DrawingId,
      normalizedType,
      displayPhase,
    );
    if (color) return color;
  }
  return getExtendedStatusColor(displayPhase, normalizedType);
}

/**
 * Get polyline style for object on map with workflow phase support.
 * Uses workflow definitions for line style (solid/dashed, thick/thin) and phase colors.
 * @param type - Object type
 * @param status - Object status (workflow phase code)
 * @param drawingId - Optional drawing id for layer-specific colors (general | stavba | montaz | zafuk | optika)
 * @param metadata - Objektová metadata (budovaWorkflowId) pro barvy variantních linií
 * @returns Leaflet PolylineOptions style
 */
export function getObjectPolylineStyle(
  type: string,
  status: string | null | undefined,
  drawingId?: string,
  metadata?: unknown
): { color: string; weight: number; opacity: number; dashArray?: string } {
  const normalizedType = (type ?? '').trim().toUpperCase();
  const color = getStatusColorForMap(status ?? null, normalizedType, drawingId, metadata);
  let weight = MAP_OBJECT_LINE_WEIGHT_DEFAULT;
  let dashArray: string | undefined;
  let opacity = MAP_OBJECT_LINE_OPACITY;

  // INFO: tenká plná linie (kruhy, vedoucí čáry)
  if (normalizedType === 'INFO') {
    return { color, weight: MAP_OBJECT_LINE_WEIGHT_THIN, opacity: MAP_OBJECT_LINE_OPACITY };
  }
  // Line style from workflow (STAVAJICI = solid thick informative route, hasWorkflow=false).
  const workflow = getWorkflowForType(normalizedType);
  if (workflow) {
    const lineStyle = getLineStyle(normalizedType);
    const lineWeight = workflow.lineWeight || 'thin';
    weight = lineWeight === 'thick' ? MAP_OBJECT_LINE_WEIGHT_THICK : MAP_OBJECT_LINE_WEIGHT_THIN;
    dashArray = lineStyle === 'dashed' ? '10, 5' : undefined;
    if (normalizedType === 'STAVAJICI') {
      opacity = MAP_OBJECT_LINE_OPACITY_INFORMATIVE_ROUTE;
    }
  }
  return {
    color,
    weight,
    opacity,
    dashArray
  };
}

/**
 * Seřadí položky filtru fází podle pořadí ve workflow (první fáze nahoře, poslední dole).
 * Kódy mimo definici workflow (včetně `__MANUAL__`) končí za známými fázemi; stabilní tie-break podle kódu.
 */
export function sortPhaseFilterOptionsByWorkflowOrder(
  objectType: string,
  options: { value: string; label: string }[],
): { value: string; label: string }[] {
  const orderByCode = new Map(
    getAllPhasesForType(objectType).map((phase, index) => [phase.code, index]),
  );
  const phaseIndex = (code: string): number => {
    if (code === '__MANUAL__') return 200000;
    const i = orderByCode.get(code);
    return i !== undefined ? i : 100000;
  };
  return [...options].sort((a, b) => {
    const da = phaseIndex(a.value);
    const db = phaseIndex(b.value);
    if (da !== db) return da - db;
    return a.value.localeCompare(b.value, 'cs');
  });
}

export interface WorkflowStatusDefinitionLike {
  code: string;
  czechName: string;
  czechDescription: string;
  englishDescription: string;
  color: string;
  meaning: string;
}

function normalizeWorkflowStatusFilterLabel(label: string): string {
  return label.trim().replace(/\s+/g, ' ').toLocaleLowerCase('cs');
}

/**
 * Build filter definitions from all workflow phases (including BUDOVA branches).
 * Equivalent phase names merge into one filter option with CSV codes.
 */
export function buildWorkflowStatusDefinitionsForFilter(
  phasesInOrder: WorkflowPhase[],
  statusDefinitions: WorkflowStatusDefinitionLike[] = [],
): WorkflowStatusDefinitionLike[] {
  const statusByCode = new Map(statusDefinitions.map((status) => [status.code, status]));
  const groups = new Map<string, WorkflowStatusDefinitionLike & { codes: string[] }>();

  for (const phase of phasesInOrder) {
    const status = statusByCode.get(phase.code);
    const czechName = status?.czechName || phase.name || getPhaseName(phase.code);
    const key = normalizeWorkflowStatusFilterLabel(czechName);
    const existing = groups.get(key);
    if (existing) {
      if (!existing.codes.includes(phase.code)) existing.codes.push(phase.code);
      existing.code = existing.codes.join(',');
      continue;
    }
    groups.set(key, {
      code: phase.code,
      codes: [phase.code],
      czechName,
      czechDescription: status?.czechDescription || '',
      englishDescription: status?.englishDescription || '',
      color: status?.color || phase.color || PHASE_COLORS.GRAY,
      meaning: status?.meaning || 'workflow_phase',
    });
  }

  return [...groups.values()].map((group) => ({
    code: group.code,
    czechName: group.czechName,
    czechDescription: group.czechDescription,
    englishDescription: group.englishDescription,
    color: group.color,
    meaning: group.meaning,
  }));
}

